import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Grid from "@mui/material/Grid2";
import TextField from "@mui/material/TextField";
import { gql } from "@apollo/client";
import { usePersistFhirQueryMutation } from "~/graphql/__generated__/graphql";
import { useState } from "react";

type PersistFhirQueryMenuItemProps = {
  open: boolean;
  onClose: () => void;
};

const textAreaSx = {
  "& .MuiInputBase-root": {
    alignItems: "flex-start",
    height: 280,
    overflow: "hidden",
  },
  "& textarea": {
    fontFamily: "monospace",
    height: "100% !important",
    overflow: "auto !important",
  },
};

const defaultBundlePlaceholder = `{
  "resourceType": "Bundle",
  "type": "searchset",
  "entry": [
    {
      "fullUrl": "https://example.org/fhir/Patient/example-1",
      "resource": {
        "resourceType": "Patient",
        "id": "example-1",
        "name": [{ "given": ["Jane"], "family": "Doe" }],
        "birthDate": "1985-03-10",
        "gender": "female"
      }
    }
  ]
}`;

export default function PersistFhirQueryMenuItem({ open, onClose }: PersistFhirQueryMenuItemProps) {
  const [patientId, setPatientId] = useState("");
  const [queryText, setQueryText] = useState("Patient?_count=2");
  const [sourceSystem, setSourceSystem] = useState("https://example.org/fhir");
  const [responseBody, setResponseBody] = useState("");
  const [validationError, setValidationError] = useState<string | null>(null);

  const [persistFhirQuery, { loading, data, error: mutationError, reset }] = usePersistFhirQueryMutation();

  const output = data?.persistFhirQuery != null
    ? JSON.stringify(data.persistFhirQuery, null, 2)
    : "";

  const displayError = validationError ?? mutationError?.message ?? null;

  const clearMutationState = () => {
    setValidationError(null);
    reset();
  };

  const handleSubmit = () => {
    clearMutationState();

    const parsedPatientId = Number.parseInt(patientId, 10);
    if (!Number.isFinite(parsedPatientId) || parsedPatientId <= 0) {
      setValidationError("patientId must be a positive integer");
      return;
    }

    if (!responseBody.trim()) {
      setValidationError("responseBody is required");
      return;
    }

    try {
      JSON.parse(responseBody);
    } catch {
      setValidationError("responseBody must be valid JSON");
      return;
    }

    void persistFhirQuery({
      variables: {
        patientId: parsedPatientId,
        queryText,
        sourceSystem,
        responseBody,
      },
    });
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
      <DialogTitle>Persist FHIR Query</DialogTitle>
      <DialogContent sx={{ overflow: "auto" }}>
        {displayError &&
          <Alert severity="error" sx={{ mb: 2 }}>
            {displayError}
          </Alert>
        }
        <Grid container spacing={2} sx={{ pt: 1 }}>
          <Grid size={{ xs: 12, md: 4 }}>
            <TextField
              label="Patient ID"
              value={patientId}
              onChange={(event) => {
                setPatientId(event.target.value);
                clearMutationState();
              }}
              type="number"
              fullWidth
            />
          </Grid>
          <Grid size={{ xs: 12, md: 8 }}>
            <TextField
              label="Query text"
              value={queryText}
              onChange={(event) => {
                setQueryText(event.target.value);
                clearMutationState();
              }}
              fullWidth
            />
          </Grid>
          <Grid size={12}>
            <TextField
              label="Source system"
              value={sourceSystem}
              onChange={(event) => {
                setSourceSystem(event.target.value);
                clearMutationState();
              }}
              fullWidth
              slotProps={{
                htmlInput: {
                  "data-testid": "persist-fhir-source-system",
                },
              }}
            />
          </Grid>
          <Grid size={12}>
            <TextField
              label="Response body (FHIR JSON)"
              value={responseBody}
              onChange={(event) => {
                setResponseBody(event.target.value);
                clearMutationState();
              }}
              multiline
              fullWidth
              placeholder={defaultBundlePlaceholder}
              sx={textAreaSx}
            />
          </Grid>
          <Grid size={12}>
            <TextField
              label="Result"
              value={output}
              multiline
              fullWidth
              sx={textAreaSx}
              slotProps={{
                input: {
                  readOnly: true,
                },
              }}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || !patientId.trim() || !queryText.trim() || !sourceSystem.trim() || !responseBody.trim()}
        >
          {loading ? <CircularProgress size={20} color="inherit" /> : "Persist"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

gql(`
  mutation PersistFhirQuery(
    $patientId: Int!
    $queryText: String!
    $sourceSystem: String!
    $responseBody: String!
  ) @permission(requires: debugApps) {
    persistFhirQuery(
      patientId: $patientId
      queryText: $queryText
      sourceSystem: $sourceSystem
      responseBody: $responseBody
    ) {
      fhirQueryId
      success
    }
  }
`);